import { useContext, useEffect, useState } from 'react';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import useMap from '@alltrails/maps/hooks/useMap';
import { GeoLocationContext } from 'components/explore/GeoLocationProvider';
import useIsochroneSearch from 'hooks/useIsochroneSearch';
import { FullscreenSearchApp } from 'react_components/search/fullscreen/search_app';
import useFeatures from 'hooks/useFeatures';
import useUser from 'hooks/useUser';
import ListMethods from 'types/ListMethods';
import { MapExpandContext } from 'components/MapExpandControl/MapExpandProvider';
import { useIntl } from '@alltrails/shared/react-intl';

type Props = {
  belongsToCurrentUser: boolean;
  isMobileWidth: boolean;
  latestListAction: any;
  listId: any;
  listItems: any;
  listMethods: ListMethods;
  lists: any;
  page: string;
  updatePage: any;
  userListItems: any;
};

/**
 * NewFullscreenSearchApp wraps the legacy React class component FullscreenSearchApp
 * with a new functional component that can access hooks.
 *
 * Over time functionality from FullscreenSearchApp should be refactored. For
 * now this can (hopefully) be a shim to help provide modern functionality.
 *
 * @param {props} Props
 * @returns JSX.Element
 */
export default function NewFullscreenSearchApp({
  belongsToCurrentUser,
  isMobileWidth,
  latestListAction,
  listId,
  listItems,
  listMethods,
  lists,
  page,
  updatePage,
  userListItems,
  ...rest
}: Props) {
  const user = useUser(); // rely on useUser hook over context.currentUser now that we support client-side-only login
  const [distanceAwayValue, setDistanceAwayValue] = useState<number>();
  const [isMapReady, setIsMapReady] = useState(false);
  const { geoLocation, getGeoLocation } = useContext(GeoLocationContext);
  const intl = useIntl();
  const {
    isLoading: isLoadingIsochrone,
    isochroneData,
    insidePolygon,
    isochroneBoundingBox
  } = useIsochroneSearch({ distanceAwayValue, geoLocation });

  useEffect(() => {
    if (distanceAwayValue && distanceAwayValue !== -1) {
      // TODO DISCO-874 re-use functionality in SliderGroup to present a modal to
      // users about geo location if needed.
      getGeoLocation();
    }
  }, [distanceAwayValue, getGeoLocation]);

  const { isMapExpanded } = useContext(MapExpandContext);

  const features = useFeatures();
  const areNewMapsEnabled = features.includes('new_maps');

  // The @alltrails/maps package does not currently support admin features.
  // Admins will not be allowed to use those components.
  const isNewMapsPage = areNewMapsEnabled && page === PageStrings.EXPLORE_ALL_PAGE && !user?.admin;

  const newMapInstance = useMap();
  const [currentlyHoveredTrail, setCurrentlyHoveredTrail] = useState(null);
  const [shouldClearClickedResult, setShouldClearClickedResult] = useState(false);

  return (
    <FullscreenSearchApp
      {...rest}
      geoLocation={geoLocation}
      insidePolygon={insidePolygon}
      isLoadingIsochrone={isLoadingIsochrone}
      isMapExpanded={isMapExpanded}
      isMapReady={isMapReady}
      isMobileWidth={isMobileWidth}
      isochroneBoundingBox={isochroneBoundingBox}
      isochroneData={isochroneData}
      latestListAction={latestListAction}
      lists={lists}
      listItems={listItems}
      // Shim to help provide search behavior until we can break out legacy
      // algolia mixins.
      newMapInstance={newMapInstance}
      onMapReady={(newValue: boolean) => {
        setIsMapReady(newValue ?? true);
      }}
      page={page}
      areNewMapsEnabled={areNewMapsEnabled}
      isNewMapsPage={isNewMapsPage}
      userListItems={userListItems}
      listMethods={listMethods}
      listId={listId}
      belongsToCurrentUser={belongsToCurrentUser}
      updatePage={updatePage}
      onUpdateDistanceAway={(value: number) => {
        setDistanceAwayValue(value);
      }}
      user={user} // pass user object as a separate field to not conflict with older functionality
      intl={intl}
      currentlyHoveredTrail={currentlyHoveredTrail}
      updateCurrentlyHoveredTrail={setCurrentlyHoveredTrail}
      shouldClearClickedResult={shouldClearClickedResult}
      setShouldClearClickedResult={setShouldClearClickedResult}
    />
  );
}
